import { Injectable } from '@angular/core';
import { WindowRefService } from '../../services/window-ref.service';
import {IdentifiedBy, MaterialTab} from '../../entity/models/entity';
import {Agent, AlternateTitle, SearchResultConcept} from 'search/models/search-results';
import { FormatGroupFullRecord, FullRecordInstance, MetadataInput } from '../models/full-record';
import { metadataLanguageMap } from '../utils/metadata-language-map';
const jsonld = require('jsonld');

@Injectable({
  providedIn: 'root',
})
export class JsonLdService {
  public jsonLdClassName = 'jsonld-data';

  constructor(
    private windowRef: WindowRefService,
  ) {}

  public async computeCreativeWorkJsonld(metadataInput: MetadataInput) {
    const inputData: MetadataInput = {...metadataInput};
    const instance: FullRecordInstance = this.getInstance(inputData.recordId, inputData.fullRecord.instances);
    const additionalType = this.parseAdditionalType(inputData.materialTabs, inputData.recordId);
    const creativeWorkSpec = {
      '@type': 'CreativeWork',
      'http://schema.org/name': this.parseName(inputData.fullRecord),
      'http://schema.org/author': this.parseAuthor(inputData.fullRecord),
      'http://schema.org/about': this.parseAbout(inputData.fullRecord.concept),
      'http://schema.org/description': this.parseSummary(inputData.fullRecord),
      'http://schema.org/publisher': instance?.provisionActivityStatement,
      'http://schema.org/datePublished': instance?.publicationDate,
      'http://schema.org/identifier': this.parseIdentifiers(inputData.fullRecord.identifiedBy),
      'http://schema.org/contributor': this.parseContributors(inputData.fullRecord.agent?.slice(1, inputData.fullRecord.agent.length)),
      'http://schema.org/alternateName': this.parseAlternateName(inputData.fullRecord),
      'http://schema.org/additionalType': additionalType,
      'http://schema.org/inLanguage': this.getLanguageCode(inputData.language, metadataLanguageMap),
      'http://schema.org/temporalCoverage': instance?.publicationDate
    };
    return await jsonld.compact(creativeWorkSpec, {'@context': 'https://schema.org/'});
  }

  public setJsonLd(schema: Record<string, any>): void {
    if (!schema) {
      return;
    }

    this.removeJsonLdData();
    const scriptElement = this.windowRef.nativeWindow().document.createElement('script');
    const scriptType = 'application/ld+json';

    scriptElement.setAttribute('class', this.jsonLdClassName);
    scriptElement.type = scriptType;
    scriptElement.text = JSON.stringify(schema);
    this.windowRef.nativeWindow().document.head.appendChild(scriptElement);
  }

  public removeJsonLdData(): void {
    const elements = [...Array.from(this.windowRef.nativeWindow().document.head.getElementsByClassName(this.jsonLdClassName))];

    elements.forEach(el => this.windowRef.nativeWindow().document.head.removeChild(el));
  }

  public parseName(data: FormatGroupFullRecord): string | [string, string] {
    if (!data.title?.[0]?.alternateTitle || (data.title[0].alternateTitle as AlternateTitle[]).length === 0) {
      return data.title?.[0]?.label;
    }

    if (data.title?.[0]?.alternateTitle && Array.isArray(data.title[0].alternateTitle)) {
      return [data.title[0].alternateTitle[0].label, data.title[0].label];
    }

    if (data.title?.[0]?.alternateTitle && !Array.isArray(data.title[0].alternateTitle)) {
      return [data.title[0].alternateTitle.label, data.title[0].label];
    }
  }

  public parseAlternateName(data: FormatGroupFullRecord): string {
    if (!data.title?.[0]?.alternateTitle || (data.title[0].alternateTitle as AlternateTitle[]).length === 0) {
      return undefined;
    }

    return Array.isArray(data.title[0].alternateTitle) ?
      (data.title[0].alternateTitle as AlternateTitle[])[0].label : (data.title[0].alternateTitle as AlternateTitle).label;
  }

  public parseAuthor(data: FormatGroupFullRecord): string | [string, string] {
    return data.primaryAgent?.alternateAgent ? [data.primaryAgent?.alternateAgent.label, data.primaryAgent?.label] : data.primaryAgent?.label;
  }

  public parseSummary(data: FormatGroupFullRecord): string | string[] {
    if (data.work && data.work[0]?.item?.summary) {
      return data.work[0]?.item?.summary[0]?.alternateSummary ?
        [data.work[0]?.item?.summary[0]?.alternateSummary.label, data.work[0]?.item?.summary[0]?.label] :
        data.work[0]?.item?.summary[0]?.label;
    }
  }

  public parseContributors(data: Agent[]): string[] {
    if (!data || data.length === 0) {
      return ;
    }
    const parsedContributors: string[] = [];

    data.forEach((contributor: Agent) => {
      if (contributor.item?.alternateAgent) {
        parsedContributors.push(contributor.item?.alternateAgent.label, contributor.item?.label);
      } else {
        parsedContributors.push(contributor.item?.label);
      }
    });

    return parsedContributors;
  }

  public parseAbout(data: SearchResultConcept[]): string[] {
    if (!data || data.length === 0) {
      return ;
    }
    const parsedAbout: string[] = [];

    data.forEach((concept: SearchResultConcept) => {
      if (concept.item?.alternateConcept) {
        parsedAbout.push(concept.item?.alternateConcept.label, concept.item?.label);
      } else {
        parsedAbout.push(concept.item?.label);
      }
    });

    return parsedAbout;
  }

  public getInstance(recordId: string, instances: FullRecordInstance[]): FullRecordInstance {
    if (instances && instances.length) {
      return instances.filter((instance: FullRecordInstance) => instance.recordId[0] === recordId)[0];
    }
  }

  public parseAdditionalType(materialTabs: MaterialTab[], recordId: string): string | undefined {
    const matchedMaterialTab: MaterialTab = materialTabs?.find(materialTab => materialTab.recordIds.includes(recordId));

    return matchedMaterialTab ? matchedMaterialTab.name : undefined;
  }

  public parseIdentifiers(identifiedByData: IdentifiedBy[]): string[] {
    if (!identifiedByData || identifiedByData.length === 0) {
      return ;
    }
    const identifierValues: string[] = [];

    identifiedByData.forEach((identifier: IdentifiedBy) => {
      if (identifier.type !== 'isbn') {
        identifierValues.push(identifier.value);
      }
    });

    return identifierValues;
  }

  public getLanguageCode(languageValue: string, languageMap: { [key: string]: string }): string | undefined {
    return languageMap[languageValue];
  }
}
